/**
 * 수정제안 시각 표시 캔버스 — 필드 우선 방식 (M15 / A10, 재설계).
 *
 * 우측 패널에서 필드를 선택(startField)하면 그 필드가 그리기 대상이 되고,
 * 캔버스 상단 힌트가 "라벨 → 값" 순서를 안내한다. 영역을 그리면 그룹이 확정된다.
 *   - 파란 점선 = 라벨, 빨간 실선 = 값.
 *   - 좌표는 자연 px 로 저장 (coords.ts) — 서버 burn-in 과 일치.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

import { FIELD_META_MAP } from "../fieldLabels";
import ImagePagePreview from "../ImagePagePreview";
import { usePagesMeta } from "../usePagesMeta";
import { bboxToPercentStyle, clientToNatural, isMeaningfulBox, rectFromPoints } from "./coords";
import type { Bbox, ClientAnnotation, Point } from "./types";
import type { UseAnnotationDrawing } from "./useAnnotationDrawing";

interface Props {
  jobId: string;
  fileId: string;
  drawing: UseAnnotationDrawing;
}

export default function AnnotationCanvas({ jobId, fileId, drawing }: Props) {
  const { t } = useTranslation();
  const meta = usePagesMeta(jobId, fileId);
  const pageCount = meta.data?.page_count ?? 1;
  const [page, setPage] = useState(1);
  const safePage = Math.max(1, Math.min(page, pageCount));

  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [dragCurrent, setDragCurrent] = useState<Point | null>(null);

  const isDrawing = drawing.activeField !== null;
  const activeLabel = drawing.activeField
    ? t(FIELD_META_MAP[drawing.activeField].labelI18nKey)
    : "";
  // 필드별 맞춤 예시 (제목/값). 미정의 필드는 빈 문자열로 안전 처리.
  const labelHint = drawing.activeField
    ? t(`corrections.fieldHints.${drawing.activeField}.label`, "")
    : "";
  const valueHint = drawing.activeField
    ? t(`corrections.fieldHints.${drawing.activeField}.value`, "")
    : "";

  const draftBox: Bbox | null = useMemo(() => {
    if (!dragStart || !dragCurrent || !natural) return null;
    return rectFromPoints(dragStart, dragCurrent, natural.w, natural.h);
  }, [dragStart, dragCurrent, natural]);

  const toNatural = useCallback(
    (clientX: number, clientY: number): Point | null => {
      const el = layerRef.current;
      if (!el || !natural) return null;
      const rect = el.getBoundingClientRect();
      return clientToNatural(clientX, clientY, rect, natural.w, natural.h);
    },
    [natural],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDrawing || e.button !== 0) return;
    const p = toNatural(e.clientX, e.clientY);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragStart(p);
    setDragCurrent(p);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    const p = toNatural(e.clientX, e.clientY);
    if (p) setDragCurrent(p);
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const box = draftBox;
    setDragStart(null);
    setDragCurrent(null);
    if (!box || !isMeaningfulBox(box)) return;
    if (drawing.stage === "label") {
      drawing.commitLabel(box);
    } else {
      drawing.commitValue(box, safePage);
    }
  };

  const currentGroups = drawing.groups.filter((g) => g.page === safePage);

  return (
    <div className="flex h-full flex-col">
      {/* toolbar */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-1.5 text-xs">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={safePage <= 1}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          aria-label={t("preview.toolbar.prevPage")}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[40px] text-center tabular-nums">
          {safePage} / {pageCount}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={safePage >= pageCount}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          aria-label={t("preview.toolbar.nextPage")}
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {/* 색상 범례 (그리기 중 상시) */}
        {isDrawing && (
          <span className="ml-auto text-[11px] text-slate-500">
            {t("corrections.canvas.legend")}
          </span>
        )}
      </div>

      {/* 이미지 + 그리기 레이어 */}
      <div className="relative flex-1 overflow-auto bg-slate-100 p-3">
        {/* 강조 안내 배너 (그리기 중) — PDF 위에 떠서 다음 동작을 명확히 안내 */}
        {isDrawing && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center p-3">
            <div
              className={cn(
                "pointer-events-auto w-full max-w-xl rounded-lg border-2 px-4 py-3 shadow-lg",
                drawing.stage === "label"
                  ? "border-blue-400 bg-blue-50"
                  : "border-red-400 bg-red-50",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-sm font-semibold",
                      drawing.stage === "label" ? "text-blue-800" : "text-red-800",
                    )}
                  >
                    {drawing.stage === "label"
                      ? t("corrections.canvas.stepLabelTitle", { field: activeLabel })
                      : t("corrections.canvas.stepValueTitle", { field: activeLabel })}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {drawing.stage === "label"
                      ? t("corrections.canvas.stepLabelDesc", { hint: labelHint })
                      : t("corrections.canvas.stepValueDesc", { hint: valueHint })}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  {drawing.stage === "label" && (
                    <button
                      type="button"
                      onClick={drawing.skipLabel}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100"
                    >
                      {t("corrections.canvas.skipLabel")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={drawing.cancelField}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                  >
                    {t("common.actions.cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <ImagePagePreview
          jobId={jobId}
          fileId={fileId}
          page={safePage}
          imgStyle={{ width: "100%", height: "auto", maxWidth: "100%" }}
          onImageLoad={(w, h) => setNatural({ w, h })}
        >
          {natural && (
            <div
              ref={layerRef}
              className={cn(
                "absolute inset-0",
                isDrawing ? "pointer-events-auto cursor-crosshair" : "pointer-events-none",
              )}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {currentGroups.map((g) => (
                <GroupBoxes
                  key={g.id}
                  group={g}
                  label={t(FIELD_META_MAP[g.field].labelI18nKey)}
                  naturalW={natural.w}
                  naturalH={natural.h}
                />
              ))}

              {draftBox && (
                <div
                  className={cn(
                    "absolute border-2",
                    drawing.stage === "label"
                      ? "border-dashed border-blue-500 bg-blue-400/10"
                      : "border-solid border-red-500 bg-red-400/10",
                  )}
                  style={bboxToPercentStyle(draftBox, natural.w, natural.h)}
                />
              )}
            </div>
          )}
        </ImagePagePreview>
      </div>
    </div>
  );
}

function GroupBoxes({
  group,
  label,
  naturalW,
  naturalH,
}: {
  group: ClientAnnotation;
  label: string;
  naturalW: number;
  naturalH: number;
}) {
  const valueStyle = bboxToPercentStyle(group.valueBbox, naturalW, naturalH);
  return (
    <>
      {group.labelBbox && (
        <div
          className="absolute border-2 border-dashed border-blue-500 bg-blue-400/5"
          style={bboxToPercentStyle(group.labelBbox, naturalW, naturalH)}
        />
      )}
      <div
        className="absolute border-2 border-solid border-red-500 bg-red-400/5"
        style={valueStyle}
      >
        <span className="absolute -top-4 left-0 whitespace-nowrap rounded bg-red-500 px-1 text-[10px] text-white">
          {label}
        </span>
      </div>
    </>
  );
}
